import { cn } from "@/lib/utils.js"

export type DecisionValue = "ALLOW" | "PREPAYMENT" | "REVIEW"

/**
 * A decision wears its colour: dot and word both, every time it appears. The
 * word still carries the status — colour never does it alone — but the colour
 * is what lets a support person scan forty rows for the one that blocked.
 * The raw API value stays reachable through `title`, so a ticket can quote it.
 */
const DECISIONS: Record<DecisionValue, { label: string; dot: string; text: string }> = {
  ALLOW: { label: "Odroczenie", dot: "bg-allow", text: "text-allow" },
  PREPAYMENT: { label: "Przedpłata", dot: "bg-prepay", text: "text-prepay" },
  REVIEW: { label: "Do decyzji", dot: "bg-review", text: "text-review" },
}

export function DecisionBadge({
  decision,
  size = "md",
}: {
  decision: DecisionValue
  size?: "md" | "lg"
}) {
  const spec = DECISIONS[decision] ?? DECISIONS.PREPAYMENT
  return (
    <span
      title={decision}
      className={cn(
        "inline-flex items-center whitespace-nowrap",
        spec.text,
        size === "lg"
          ? "gap-2.5 text-xl font-semibold tracking-tight"
          : "gap-2 text-sm font-medium",
      )}
    >
      <span
        aria-hidden="true"
        className={cn("rounded-full", spec.dot, size === "lg" ? "size-2.5" : "size-2")}
      />
      {spec.label}
    </span>
  )
}

/**
 * `degraded` is not a second decision — it is the difference between "we
 * checked and they are struck off" and "we could not check". Rare and
 * load-bearing, so it is the one state in a row that gets colour and weight.
 */
export function DegradedFlag({ degraded }: { degraded: boolean }) {
  if (!degraded) return null
  return (
    <span
      title="Odpowiedź z fail-closed, nie z kompletnych danych"
      className="inline-flex cursor-help items-center gap-1 text-[13px] font-medium text-critical"
    >
      <span aria-hidden="true">!</span>
      niepewna
    </span>
  )
}

/**
 * Codes are identifiers, so they read as mono text. **Only a veto earns colour**
 * — and that is decided by the reason's `kind`, not by guessing at the code
 * string. The old suffix test (`.endsWith(".bankruptcy")`) never matched the
 * real code `gus.bankruptcy_declared`, so a bankruptcy that blocked rendered as
 * ordinary text; and it would have reddened a code that only scored. `kind` is
 * the fact: `VETO` is the hard exclusion that actually stood alone. Where the
 * kind is not known (a bare code in a history list), the code stays neutral
 * rather than claiming a severity it cannot see — the row's decision badge
 * already carries that.
 */
export function ReasonBadge({ code, kind }: { code: string; kind?: string }) {
  const critical = kind === "VETO"
  return (
    <span
      className={cn(
        "font-mono text-[12.5px]",
        critical ? "font-medium text-critical" : "text-muted-foreground",
      )}
    >
      {code}
    </span>
  )
}
